import React from 'react';
import cx from 'classnames';
import _ from 'lodash';
import moment from 'moment';
import CalendarControls from './CalendarControls';
import CalendarActions from 'agent_portal/actions/CalendarActions';
import classes from './SmallCalendar.module.scss';

// Takes a moment, returns an array of moments. Memoized.
const monthDaysForGivenDate = date => {
  const DAYS_PER_WEEK = 7;
  const WEEKS_PER_CAL_PAGE = 6;
  const DAYS_PER_CAL_PAGE = DAYS_PER_WEEK * WEEKS_PER_CAL_PAGE;

  const startOfMonthMoment = date.clone().startOf('month');
  const numDaysToBackfill = startOfMonthMoment.weekday();
  const numDaysInMonth = startOfMonthMoment.daysInMonth();

  const daysCollection = [];
  let day;

  // Fill in days back to beginning of week (last month's days)
  for (let i = 0; i < numDaysToBackfill; i++) {
    day = startOfMonthMoment.clone().subtract(Math.abs(i - numDaysToBackfill), 'days');
    daysCollection.push(day);
  }

  // Fill in all regular days of this month
  for (let i = 0; i < numDaysInMonth; i++) {
    day = moment([startOfMonthMoment.year(), startOfMonthMoment.month(), i + 1]);
    daysCollection.push(day);
  }

  // Fill rest of week row, if necessary
  const weekSpacesLeft = daysCollection.length % DAYS_PER_WEEK;
  const lastDayOfMonth = daysCollection[daysCollection.length - 1];
  for (let i = 0; i < weekSpacesLeft; i++) {
    day = lastDayOfMonth.clone().add(i + 1, 'days');
    daysCollection.push(day);
  }

  // Fill row 6, if necessary
  if (daysCollection.length !== DAYS_PER_CAL_PAGE) {
    const lastDayAdded = daysCollection[daysCollection.length - 1];
    const fillDaysToAdd = DAYS_PER_CAL_PAGE - daysCollection.length;

    for (let i = 0; i < fillDaysToAdd; i++) {
      day = lastDayAdded.clone().add(i + 1, 'days');
      daysCollection.push(day);
    }
  }

  return daysCollection;
};

const monthObjectFromDaysCollection = ({ daysCollection, focusedDate }) => {
  const decoratedDays = daysCollection.map(day => ({
    day,
    isFocusedDay: day.isSame(focusedDate, 'day'),
    isFocusedMonth: day.isSame(focusedDate, 'month'),
  }));

  const weekChunks = _.chunk(decoratedDays, 7);
  const decoratedWeeks = weekChunks.map(weekDays => ({
    key: weekDays[0].day.format(),
    days: weekDays,
    isFocusedWeek: _.some(weekDays, day => day.isFocusedDay),
  }));

  return {
    weeks: decoratedWeeks,
  };
};

const SmallCalendar = ({ date }) => {
  const rawDays = monthDaysForGivenDate(date);
  const month = monthObjectFromDaysCollection({
    daysCollection: rawDays,
    focusedDate: date,
  });

  return (
    <div className={classes.container}>
      <CalendarControls date={date} onNavigate={CalendarActions.navigateToDate} />
      <table>
        <thead>
          <tr>
            <th>Sun</th>
            <th>Mon</th>
            <th>Tue</th>
            <th>Wed</th>
            <th>Thu</th>
            <th>Fri</th>
            <th>Sat</th>
          </tr>
        </thead>
        <tbody>
          {month.weeks.map(week => (
            <tr key={week.key} className={week.isFocusedWeek ? classes.current_week : ''}>
              {week.days.map(decoratedDay => {
                const dayClasses = cx({
                  [classes.other_month]: !decoratedDay.isFocusedMonth,
                  [classes.today]: decoratedDay.isFocusedDay,
                });

                return (
                  <td key={decoratedDay.day.format()} className={dayClasses}>
                    <div
                      className={classes.highlight}
                      onClick={() => CalendarActions.navigateToDate(decoratedDay.day)}
                    >
                      {decoratedDay.day.date()}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

SmallCalendar.displayName = 'SmallCalendar';

export default SmallCalendar;
